import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../files/images/logo.png';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useToast } from '../providers/toaster-provider';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const { showError, showSuccess } = useToast();

    const [checkToken, setCheckToken] = useApi();
    const [submitReset, setSubmitReset] = useApi();

    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);
    const [fieldError, setFieldError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) return;
        const { url, method } = buildApiRequestConfig(routes.user.checkResetToken, [{ param: 'token', value: token }]);
        setCheckToken({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleSubmit = () => {
        setFieldError('');
        if (!password || password.length < 6) {
            setFieldError('Password must be at least 6 characters');
            return;
        }
        if (password !== password2) {
            setFieldError('Passwords do not match');
            return;
        }
        const { url, method } = buildApiRequestConfig(routes.user.resetPassword);
        setSubmitReset({ url, method, body: { token, newPassword: password } });
    };

    useEffect(() => {
        const { data, isLoading, error } = submitReset;
        if (data && !isLoading) {
            setSuccess(true);
            showSuccess('Password reset successfully. You can now log in.');
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to reset password. Please try again.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitReset]);

    const hasResponded = !!checkToken.data || !!checkToken.error;
    const isValidating = !!token && !hasResponded;
    const isInvalid = !token || !!checkToken.error;

    return (
        <Box width="100vw" minHeight="100vh" bgcolor="var(--color-page-background)">
            <Box
                sx={{ position: 'absolute', top: 50, left: 0, boxSizing: 'border-box', '@media (max-width: 600px)': { top: 8, left: 8 } }}
                width="100%"
            >
                <Box maxWidth="1200px" m="0 auto" px={{ xs: '16px', sm: '64px' }} boxSizing="border-box">
                    <img src={logo} alt="FineAnts" style={{ height: 45 }} />
                </Box>
            </Box>
            <Box display="flex" minHeight="100vh" alignItems="center" justifyContent="center" px={{ xs: 3, sm: 8 }}>
                <Box width="100%" maxWidth={400}>
                    {isValidating ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress sx={{ color: 'var(--color-primary)' }} />
                        </Box>
                    ) : isInvalid ? (
                        <>
                            <Typography variant="h4" fontWeight={600} mb={1}>
                                Link expired
                            </Typography>
                            <Typography color="text.secondary" mb={3}>
                                This password reset link is invalid or has expired. Please request a new one.
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none' }}
                                onClick={() => navigate('/forgot-password')}
                            >
                                Request a new link
                            </Button>
                        </>
                    ) : success ? (
                        <>
                            <Typography variant="h4" fontWeight={600} mb={1}>
                                Password reset
                            </Typography>
                            <Typography color="text.secondary" mb={3}>
                                Your password has been reset successfully. You can now log in with your new password.
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none' }}
                                onClick={() => navigate('/login')}
                            >
                                Back to login
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="h4" fontWeight={600} mb={1}>
                                Set a password
                            </Typography>
                            <Typography color="text.secondary" mb={3}>
                                Please set a new password for your account.
                            </Typography>
                            <TextField
                                label="Create Password"
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                value={password}
                                error={!!fieldError}
                                onChange={e => setPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(s => !s)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: { background: 'var(--color-page-background)' }
                                }}
                            />
                            <TextField
                                label="Re-enter Password"
                                type={showPassword2 ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                value={password2}
                                error={!!fieldError}
                                helperText={fieldError}
                                onChange={e => setPassword2(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword2(s => !s)} edge="end">
                                                {showPassword2 ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: { background: 'var(--color-page-background)' }
                                }}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={submitReset.isLoading}
                                sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none', mt: 1 }}
                                onClick={handleSubmit}
                            >
                                {submitReset.isLoading ? 'Setting password...' : 'Set password'}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ResetPassword;
